import React, { useCallback, useMemo } from "react";

import type { OrderAttributeFragment, OrderServiceFragment } from "@stepapp/core";
import { OrderInfoItem } from "@stepapp/ui";

type Props = {
  services?: (OrderServiceFragment | null)[];
  attributes?: (OrderAttributeFragment | null)[];
  isEditable?: boolean;
  handleQuestionEdit: (typeWithIndex: string) => void;
};

const AdditionalServices = ({
  attributes,
  services,
  handleQuestionEdit,
  isEditable = true,
}: Props) => {
  const items = useMemo(() => {
    const attributeItems =
      attributes?.map((a) => ({
        type: "basic" as const,
        title: a?.attribute.name,
        answerTitle: `${a?.quantity && a?.quantity > 1 ? `${a?.quantity}x ` : ""}${a?.values?.map((value) => value?.name).join("\n") || ""}`,
        value: a?.values,
        price: a?.price,
        currency: a?.currency,
        description: a?.attribute.description || "",
        id: a?.attribute.id,
      })) || [];

    const mappedQuantities =
      services?.map((s) => ({
        variants: s?.variants,
        description: s?.service.description || "",
        title: s?.service.name,
        type: "quantity" as const,
        price: s?.price,
        currency: s?.currency,
        id: s?.service.id,
        isFree: s?.variants?.some((variant) => variant?.isFree),
      })) || [];

    return [...attributeItems, ...mappedQuantities];
  }, [attributes, services]);

  const handleEdit = useCallback(
    (questionToEdit: string) => {
      handleQuestionEdit(questionToEdit);
    },
    [handleQuestionEdit],
  );

  const renderItem = useCallback(
    (item: (typeof items)[number]) => {
      switch (item.type) {
        case "basic":
          return (
            <OrderInfoItem
              editable={isEditable}
              answer={item.answerTitle}
              onEdit={() => handleEdit(`questions.${item.id}`)}
              price={item.price}
              title={item.title}
              currency={item.currency}
              type={isEditable ? "default" : "unstyled"}
            />
          );

        case "quantity":
          return (
            <OrderInfoItem
              additionalServices={item.variants}
              editable={item?.isFree ? false : isEditable}
              onEdit={() => handleEdit(`services.${item.id}`)}
              price={item.price}
              title={item.title}
              currency={item.currency}
              type={isEditable ? "default" : "unstyled"}
            />
          );
        default:
          return null;
      }
    },
    [isEditable, handleEdit],
  );

  return (
    <>
      {items?.map((item, index) => (
        <React.Fragment key={`${item.id}${index}`}>{renderItem(item)}</React.Fragment>
      ))}
    </>
  );
};

export default React.memo(AdditionalServices);
